import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import {
  Event as RouterEvent,
  NavigationCancel,
  NavigationCancellationCode,
  NavigationSkipped,
  NavigationSkippedCode,
  NavigationStart,
  Router,
} from '@angular/router';

export const NAV_NOOP_WINDOW_MS = 1000;

/** A fixed copy of the rail's destinations, never read from the sidebar, so a drifted rail shows up in its spec. */
export const RAIL_NAV_TARGETS: Readonly<Record<string, string>> = Object.freeze({
  'app-sidebar-nav-home': '/workspaces/home',
  'app-sidebar-nav-workspaces': '/workspaces/list',
  'app-sidebar-nav-vaults': '/vaults/list',
  'app-sidebar-nav-connectors': '/workspaces/connectors',
});

const RAIL_SELECTOR = '[data-testid="app-sidebar-nav"]';
const RAIL_ITEM_SELECTOR = '[data-testid^="app-sidebar-nav-"]';
const MAX_CLASSES_LENGTH = 200;

interface ElementDescriptor {
  tag: string;
  id: string | null;
  classes: string;
  testid: string | null;
}

interface RailClick {
  item: string;
  targetUrl: string | null;
  currentUrl: string;
  input: 'pointer' | 'keyboard';
  hit: ElementDescriptor | null;
}

interface PendingNoopWindow {
  click: RailClick;
  skipped: string | null;
  timer?: ReturnType<typeof setTimeout>;
}

function describeElement(target: EventTarget | null): ElementDescriptor | null {
  if (!(target instanceof Element)) {
    return null;
  }
  const className = typeof target.className === 'string' ? target.className : (target.getAttribute('class') ?? '');
  return {
    tag: target.tagName,
    id: target.id || null,
    classes: className.slice(0, MAX_CLASSES_LENGTH),
    testid: target.getAttribute('data-testid'),
  };
}

function containsPoint(rect: DOMRect, x: number, y: number): boolean {
  return rect.width > 0 && rect.height > 0 && x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

/**
 * Console-only diagnostics for cancelled navigations and rail clicks followed by no NavigationStart.
 * Listens on the document, capture phase: an overlay over the rail becomes the click target and takes
 * the rail off the event path. Must never cancel, stop or redirect the click it observes.
 */
@Injectable({ providedIn: 'root' })
export class NavigationDiagnosticsService {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private readonly pendingWindows = new Set<PendingNoopWindow>();

  private started = false;

  public start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    try {
      const listener = (event: MouseEvent): void => this.onDocumentClick(event);
      const routerEvents = this.router.events.subscribe((event) => this.onRouterEvent(event));
      this.zone.runOutsideAngular(() => this.document.addEventListener('click', listener, { capture: true, passive: true }));
      this.destroyRef.onDestroy(() => {
        routerEvents.unsubscribe();
        this.document.removeEventListener('click', listener, { capture: true });
        this.clearPendingWindows();
      });
    } catch {
      console.warn('[nav-diagnostics-failed]', { stage: 'start' });
    }
  }

  private onRouterEvent(event: RouterEvent): void {
    try {
      if (event instanceof NavigationStart) {
        this.clearPendingWindows();
      } else if (event instanceof NavigationSkipped) {
        this.pendingWindows.forEach((noopWindow) => (noopWindow.skipped = NavigationSkippedCode[event.code] ?? null));
      } else if (event instanceof NavigationCancel) {
        const code = event.code === undefined ? null : (NavigationCancellationCode[event.code] ?? null);
        console.info('[nav-cancel]', { url: event.url, code, reason: event.reason });
      }
    } catch {
      console.warn('[nav-diagnostics-failed]', { stage: 'router-event' });
    }
  }

  private onDocumentClick(event: MouseEvent): void {
    try {
      if (event.button !== 0) {
        return;
      }
      const item = this.resolveRailItem(event);
      if (!item || (item.tagName === 'A' && (event.ctrlKey || event.shiftKey || event.altKey || event.metaKey))) {
        return;
      }
      const pointer = event.detail > 0;
      const testid = item.getAttribute('data-testid') ?? '';
      this.openWindow({
        item: testid,
        targetUrl: RAIL_NAV_TARGETS[testid] ?? null,
        currentUrl: this.router.url,
        input: pointer ? 'pointer' : 'keyboard',
        hit: describeElement(pointer ? this.document.elementFromPoint(event.clientX, event.clientY) : event.target),
      });
    } catch {
      console.warn('[nav-diagnostics-failed]', { stage: 'click' });
    }
  }

  /** Keyboard activation reports 0/0 coordinates, so only a pointer click may fall back to hit-testing. */
  private resolveRailItem(event: MouseEvent): Element | null {
    const direct = event.target instanceof Element ? event.target.closest(`${RAIL_SELECTOR} ${RAIL_ITEM_SELECTOR}`) : null;
    if (direct || event.detail <= 0) {
      return direct;
    }
    const rail = this.document.querySelector(RAIL_SELECTOR);
    if (!rail || !containsPoint(rail.getBoundingClientRect(), event.clientX, event.clientY)) {
      return null;
    }
    const items = Array.from(rail.querySelectorAll(RAIL_ITEM_SELECTOR));
    return items.find((candidate) => containsPoint(candidate.getBoundingClientRect(), event.clientX, event.clientY)) ?? null;
  }

  private openWindow(click: RailClick): void {
    const noopWindow: PendingNoopWindow = { click, skipped: null };
    noopWindow.timer = this.zone.runOutsideAngular(() => setTimeout(() => this.reportNoop(noopWindow), NAV_NOOP_WINDOW_MS));
    this.pendingWindows.add(noopWindow);
  }

  private reportNoop(noopWindow: PendingNoopWindow): void {
    this.pendingWindows.delete(noopWindow);
    console.warn('[nav-noop]', { ...noopWindow.click, skipped: noopWindow.skipped });
  }

  private clearPendingWindows(): void {
    this.pendingWindows.forEach((noopWindow) => clearTimeout(noopWindow.timer));
    this.pendingWindows.clear();
  }
}
